import { CertificateBanner, CERTIFICATE_LAYOUT, SimpleText, useIntl, useMe, useMetrika } from 'platform-components';
import { ReduxAwardsStore } from 'platform-components/src/components/awards/typings';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { downloadArchive } from 'utils/download-file';

import { getCanLoadStudentsAwards, getAwardsCertificatesByStudentId } from '../selectors';
import { GenerateCertsZipButton } from '../__generate-certs-zip-button/results__generate-certs-zip-button';

interface Props {
    eventSlug?: string;
}

interface Params {
    courseId: string;
}

export const StudentsAwardsBanner = ({ eventSlug }: Props) => {
    const { formatMessage } = useIntl();
    const { sendGoal } = useMetrika();
    const me = useMe();

    const params = useParams<Params>();
    const courseId = Number(params.courseId);

    const certificates = useSelector((storeState: ReduxAwardsStore) =>
        getAwardsCertificatesByStudentId(storeState, { courseId })
    );
    const canDownloadAwards = useSelector((storeState: ReduxAwardsStore) =>
        getCanLoadStudentsAwards(storeState, { courseId })
    );

    const i18n = useCallback(
        (id: string) => formatMessage({ id: `specCourse.lessonView.studentsAwards.${id}` }),
        [formatMessage]
    );

    const [archiveIsLoading, setArchiveIsLoading] = useState(false);

    const handleClick = useCallback(() => {
        setArchiveIsLoading(true);

        sendGoal('oly_download_superprize', {
            role: 'teacher',
            userID: me?.id,
            event: eventSlug,
        });

        const fileName = i18n('archive.fileName');
        downloadArchive(
            Object.values(certificates).map((x) => x.certificate!),
            fileName
        ).finally(() => {
            setArchiveIsLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [certificates, i18n]);

    const descriptionText = canDownloadAwards
        ? i18n('banner.description')
        : i18n('banner.certificateGenerationDescription');

    let hasBeGeneratedPdfs = false,
        hasFeGeneratedPdfs = false;

    for (const { template, certificate } of Object.values(certificates)) {
        if (template) {
            hasFeGeneratedPdfs = true;
        } else if (certificate) {
            hasBeGeneratedPdfs = true;
        }
    }

    useEffect(() => {
        if (hasFeGeneratedPdfs && hasBeGeneratedPdfs) {
            window.Ya.Rum.logError({
                message: 'Archive should not have both FE- and BE-generated pdfs',
                additional: {
                    courseId,
                    slug: eventSlug,
                },
            });
        }
    }, [hasFeGeneratedPdfs, hasBeGeneratedPdfs, courseId, eventSlug]);

    return (
        <CertificateBanner
            buttonProps={{ progress: archiveIsLoading }}
            buttonText={i18n('banner.button')}
            description={<SimpleText type={SimpleText.TYPE.MARKDOWN}>{descriptionText}</SimpleText>}
            headerText={i18n('banner.header')}
            imageSrc={i18n('banner.previewImageSrc')}
            layout={CERTIFICATE_LAYOUT.WIDE}
            onClick={handleClick}
            showButton={
                canDownloadAwards &&
                (!hasFeGeneratedPdfs || (
                    <GenerateCertsZipButton certificates={certificates} courseId={courseId}>
                        {i18n('banner.button')}
                    </GenerateCertsZipButton>
                ))
            }
            theme="blue"
        />
    );
};
